import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2, Pencil, UserPlus, X, Save } from 'lucide-react'
import MemberRowActions from '../MemberRowActions/MemberRowActions'
import MemberActivityLog from '../MemberActivityLog/MemberActivityLog'
import {
    postMemberAction,
    memberPatchFromAutoWhatsapp,
    resolveBusinessCategory,
    fetchTaxonomyCategories,
    isLeadOnlyMember,
    isNonWhatsappMarketingContext,
    resolveNonWhatsappLeadId,
    normalizeCapturedWhatsapp,
    formatCapturedWhatsappE164,
    convertNonWhatsappMemberFromLead,
    memberEntitySubtitleExtra,
} from '../../utils/marketingApi'
import './MemberCustomerPanel.css'

function memberDisplayName(member) {
    return (member?.name || member?.business_name || member?.phone || 'Member').trim()
}

const DOMAINS = [
    'AGRICULTURE',
    'FISHERIES & COASTAL',
    'FOOD & BEVERAGE',
    'CONSTRUCTION & WOODWORKS',
    'INDUSTRIAL & ENGINEERING',
    'ELECTRONICS',
    'AUTOMOTIVE',
    'APPAREL & GENERAL RETAIL',
    'HEALTHCARE & MEDICAL',
    'EVENTS & HOSPITALITY',
    'EDUCATION & INSTITUTIONS',
    'SERVICES, ADVERTISING',
    'OTHERS',
]

const LANG_OPTIONS = [
    { value: 'kannada', label: 'Kannada (default)' },
    { value: 'roman_kannada', label: 'Roman Kannada' },
    { value: 'hindi', label: 'Hindi' },
    { value: 'english', label: 'English' },
]

function buildLeadDraft(member) {
    return {
        name: member?.name || member?.voice_contact_name || member?.business_name || '',
        phone: member?.phone_e164 || member?.phone || '',
        business_name: member?.business_name || member?.voice_business_label || member?.name || '',
        business_category: member?.business_category || member?.category || member?.matched_category || '',
        domain: member?.domain || '',
        matched_category: member?.matched_category || '',
        address: member?.address || '',
        city: member?.city || '',
        pincode: member?.pincode || '',
        preferred_language: member?.preferred_language || 'kannada',
        rating: member?.rating ?? 5,
        star: !!member?.star,
        source: member?.source || '',
        scrape_name: member?.scrape_name || '',
        contact_status: member?.contact_status || '',
        voice_contact_name: member?.voice_contact_name || '',
        voice_business_label: member?.voice_business_label || '',
        captured_whatsapp_number: member?.captured_whatsapp_number || '',
    }
}

function remarksHistoryEntries(text, max = 3) {
    if (!text?.trim()) return []
    return text.split(/\n{2,}/).filter(Boolean).slice(-max)
}

async function resolveCustomerIdByPhone(customerApiBase, phone, authHeaders = {}) {
    const digits = String(phone || '').replace(/\D/g, '')
    const query = digits.length >= 10 ? digits.slice(-10) : digits
    if (!query) return null
    try {
        const searchRes = await fetch(
            `${customerApiBase}/users/search?query=${encodeURIComponent(query)}`,
            { headers: authHeaders },
        )
        if (!searchRes.ok) return null
        const searchData = await searchRes.json()
        const user = (searchData?.users || [])[0]
        if (!user?.id) return null
        const custRes = await fetch(`${customerApiBase}/users/${user.id}/customer`, {
            headers: authHeaders,
        })
        if (!custRes.ok) return null
        const custData = await custRes.json()
        const cid = Number(custData?.customer_id)
        return Number.isFinite(cid) && cid > 0 ? cid : null
    } catch {
        return null
    }
}

function profileDraftFromMember(member) {
    return {
        name: member?.name || member?.voice_contact_name || '',
        phone: member?.phone_e164 || member?.phone || '',
        business_name: member?.business_name || member?.voice_business_label || member?.name || '',
        business_category: member?.business_category || member?.matched_category || '',
        matched_category: member?.matched_category || '',
        domain: member?.domain || '',
        address: member?.address || '',
        city: member?.city || '',
        pincode: member?.pincode || '',
        preferred_language: member?.preferred_language || 'kannada',
        star: !!member?.star,
        rating: member?.rating ?? 5,
    }
}

function ProfileDisplayGrid({ draft, nonWaPhone = '' }) {
    return (
        <dl className="mcp-details-grid">
            <div className="mcp-details-row"><dt>Name</dt><dd>{draft.name || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Phone (WhatsApp)</dt><dd>{draft.phone || '—'}</dd></div>
            {nonWaPhone ? (
                <div className="mcp-details-row"><dt>Non-WA number</dt><dd>{nonWaPhone}</dd></div>
            ) : null}
            <div className="mcp-details-row"><dt>Business</dt><dd>{draft.business_name || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Category</dt><dd>{draft.business_category || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Domain</dt><dd>{draft.domain || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Matched</dt><dd>{draft.matched_category || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Address</dt><dd>{draft.address || '—'}</dd></div>
            <div className="mcp-details-row"><dt>City</dt><dd>{draft.city || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Pincode</dt><dd>{draft.pincode || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Language</dt><dd>{draft.preferred_language || '—'}</dd></div>
            <div className="mcp-details-row"><dt>Rating</dt><dd>{draft.rating ?? 5}</dd></div>
            <div className="mcp-details-row"><dt>Star</dt><dd>{draft.star ? 'Yes' : 'No'}</dd></div>
        </dl>
    )
}

function taxonomyCategoriesForDomain(domain, taxonomy) {
    if (!domain || !taxonomy) return []
    if (taxonomy[domain]) return taxonomy[domain]
    const key = Object.keys(taxonomy).find((k) => k.toLowerCase() === String(domain).toLowerCase())
    return key ? taxonomy[key] : []
}

function ProfileFormFields({
    draft,
    setDraft,
    categoryAutoSync,
    taxonomy = {},
    phoneReadonly = false,
    hidePhone = false,
    idPrefix = 'mcp',
}) {
    const effectiveDomain = draft.domain || categoryAutoSync?.domain || 'OTHERS'
    const matchedOptions = [...new Set(taxonomyCategoriesForDomain(effectiveDomain, taxonomy))].sort()

    return (
        <div className="mcp-form-grid">
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-name`}>Name</label>
                <input
                    id={`${idPrefix}-name`}
                    value={draft.name || ''}
                    onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                />
            </div>
            {!hidePhone ? (
                <div className="mcp-field">
                    <label htmlFor={`${idPrefix}-phone`}>Phone</label>
                    <input
                        id={`${idPrefix}-phone`}
                        value={draft.phone || ''}
                        readOnly={phoneReadonly}
                        className={phoneReadonly ? 'mcp-field-readonly' : ''}
                    />
                </div>
            ) : null}
            <div className="mcp-field mcp-field--full">
                <label htmlFor={`${idPrefix}-business-name`}>Business name</label>
                <input
                    id={`${idPrefix}-business-name`}
                    value={draft.business_name || ''}
                    onChange={(e) => setDraft((p) => ({ ...p, business_name: e.target.value }))}
                />
            </div>
            <div className="mcp-field mcp-field--full">
                <label htmlFor={`${idPrefix}-business-category`}>Business category</label>
                <input
                    id={`${idPrefix}-business-category`}
                    value={draft.business_category || ''}
                    placeholder="e.g. Rice mill, Hardware store"
                    onChange={(e) => setDraft((p) => ({
                        ...p,
                        business_category: e.target.value,
                        domain: '',
                        matched_category: '',
                    }))}
                />
            </div>
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-domain`}>Domain</label>
                <select
                    id={`${idPrefix}-domain`}
                    value={draft.domain || ''}
                    onChange={(e) => setDraft((p) => ({
                        ...p,
                        domain: e.target.value,
                        matched_category: '',
                    }))}
                >
                    <option value="">
                        Auto-sync{categoryAutoSync?.domain ? `: ${categoryAutoSync.domain}` : ''}
                    </option>
                    {DOMAINS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>
                {!draft.domain && categoryAutoSync?.domain ? (
                    <span className="mcp-auto-sync-hint">Synced from category</span>
                ) : null}
            </div>
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-matched-category`}>Matched category</label>
                <select
                    id={`${idPrefix}-matched-category`}
                    value={draft.matched_category || ''}
                    disabled={matchedOptions.length === 0}
                    onChange={(e) => setDraft((p) => ({ ...p, matched_category: e.target.value }))}
                >
                    <option value="">
                        Auto-sync
                        {(!draft.domain || draft.domain === categoryAutoSync?.domain) && categoryAutoSync?.matched_category
                            ? `: ${categoryAutoSync.matched_category}`
                            : ''}
                    </option>
                    {matchedOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
                {!draft.matched_category && categoryAutoSync?.matched_category ? (
                    <span className="mcp-auto-sync-hint">Synced from category</span>
                ) : null}
            </div>
            <div className="mcp-field mcp-field--full">
                <label htmlFor={`${idPrefix}-address`}>Address</label>
                <input
                    id={`${idPrefix}-address`}
                    value={draft.address || ''}
                    onChange={(e) => setDraft((p) => ({ ...p, address: e.target.value }))}
                />
            </div>
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-city`}>City</label>
                <input
                    id={`${idPrefix}-city`}
                    value={draft.city || ''}
                    onChange={(e) => setDraft((p) => ({ ...p, city: e.target.value }))}
                />
            </div>
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-pincode`}>Pincode</label>
                <input
                    id={`${idPrefix}-pincode`}
                    value={draft.pincode || ''}
                    onChange={(e) => setDraft((p) => ({
                        ...p,
                        pincode: e.target.value.replace(/[^\d]/g, '').slice(0, 6),
                    }))}
                />
            </div>
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-language`}>Language</label>
                <select
                    id={`${idPrefix}-language`}
                    value={draft.preferred_language || 'kannada'}
                    onChange={(e) => setDraft((p) => ({ ...p, preferred_language: e.target.value }))}
                >
                    {LANG_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
            <div className="mcp-field">
                <label htmlFor={`${idPrefix}-rating`}>Rating</label>
                <input
                    id={`${idPrefix}-rating`}
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={draft.rating ?? 5}
                    onChange={(e) => setDraft((p) => ({ ...p, rating: parseFloat(e.target.value) || 5 }))}
                />
            </div>
            <div className="mcp-field mcp-field--checkbox">
                <label htmlFor={`${idPrefix}-star`}>
                    <input
                        id={`${idPrefix}-star`}
                        type="checkbox"
                        checked={!!draft.star}
                        onChange={(e) => setDraft((p) => ({ ...p, star: e.target.checked }))}
                    />
                    Star customer
                </label>
            </div>
        </div>
    )
}

function memberPanelKey(member) {
    if (member?.id != null) return `id:${member.id}`
    const phone = member?.phone_e164 || member?.phone
    return phone ? `ph:${phone}` : ''
}

export default function MemberCustomerPanel({
    isOpen,
    onClose,
    member,
    customerApiBase,
    authToken,
    onConverted,
    onRemarksSaved,
    batchId = null,
    marketingInitiated = true,
    showCall = false,
    showToast = null,
    onActionRecorded = null,
}) {
    const [activeCustomerId, setActiveCustomerId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState(null)
    const [convertBusy, setConvertBusy] = useState(false)
    const [convertMsg, setConvertMsg] = useState(null)

    const [customerData, setCustomerData] = useState(null)
    const [isEditing, setIsEditing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [profileDraft, setProfileDraft] = useState({})
    const [profileCategoryAutoSync, setProfileCategoryAutoSync] = useState({ domain: '', matched_category: '' })

    const [leadDraft, setLeadDraft] = useState({})
    const [leadCategoryAutoSync, setLeadCategoryAutoSync] = useState({ domain: '', matched_category: '' })
    const [taxonomy, setTaxonomy] = useState({})

    const [remarks, setRemarks] = useState('')
    const [remarksDraft, setRemarksDraft] = useState('')
    const [isRemarksEditing, setIsRemarksEditing] = useState(true)
    const [remarksSaving, setRemarksSaving] = useState(false)
    const [activityRefreshKey, setActivityRefreshKey] = useState(0)

    const leadId = member?.lead_id ? Number(member.lead_id) : null
    const memberPhone = (member?.phone_e164 || member?.phone || '').trim()
    const dialedNonWaPhone = (member?.dialed_phone || member?.non_wa_phone || memberPhone).trim()
    const leadOnly = isLeadOnlyMember(member)
    const batchForNw = useMemo(() => ({
        completion_source: member?.batch_completion_source || '',
        voice_target_type: member?.batch_voice_target || '',
        filter_snapshot: member?.batch_voice_target === 'not_on_whatsapp'
            ? { import_target: 'non_whatsapp' }
            : {},
    }), [member?.batch_completion_source, member?.batch_voice_target])
    const isNwMember = isNonWhatsappMarketingContext(member, batchForNw)
    const initialCustomerId = leadOnly ? null : (member?.customer_id ? Number(member.customer_id) : null)
    const effectiveCustomerId = activeCustomerId || initialCustomerId
    const showConvert = !effectiveCustomerId && (!!leadId || !!memberPhone)
    const memberKey = useMemo(() => memberPanelKey(member), [member?.id, member?.phone_e164, member?.phone])
    const memberRef = useRef(member)
    memberRef.current = member
    const remarksHistory = useMemo(() => remarksHistoryEntries(remarks), [remarks])

    const authHeaders = useMemo(() => {
        const h = { 'Content-Type': 'application/json' }
        if (authToken) h.Authorization = `Bearer ${authToken}`
        return h
    }, [authToken])

    const runAutoMatcher = useCallback((category, businessName, contactName, setAutoSyncFn) => {
        const cat = (category || '').trim()
        if (!cat) {
            setAutoSyncFn({ domain: '', matched_category: '' })
            return undefined
        }
        const nameHint = (businessName || contactName || '').trim()
        const t = setTimeout(() => {
            resolveBusinessCategory(cat, nameHint)
                .then((data) => {
                    if (!data) return
                    setAutoSyncFn({
                        domain: data.domain || '',
                        matched_category: data.matched_category || '',
                    })
                })
                .catch(() => {})
        }, 350)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        if (!isOpen) return undefined
        let cancelled = false
        fetchTaxonomyCategories()
            .then((data) => {
                if (!cancelled && data?.taxonomy) setTaxonomy(data.taxonomy)
            })
            .catch(() => {})
        return () => { cancelled = true }
    }, [isOpen])

    const resetState = useCallback(() => {
        const m = memberRef.current
        const initCid = leadOnly ? null : (m?.customer_id ? Number(m.customer_id) : null)
        setActiveCustomerId(initCid || null)
        setError(null)
        setConvertMsg(null)
        setCustomerData(null)
        setIsEditing(false)
        setProfileDraft(profileDraftFromMember(m))
        setProfileCategoryAutoSync({ domain: '', matched_category: '' })
        setLeadDraft((prev) => {
            const built = {
                ...buildLeadDraft(m),
                domain: '',
                matched_category: '',
            }
            const typed = String(prev?.captured_whatsapp_number ?? '').trim()
            const fromMember = String(m?.captured_whatsapp_number ?? '').trim()
            if (typed && typed !== fromMember) {
                built.captured_whatsapp_number = prev.captured_whatsapp_number
            }
            return built
        })
        setLeadCategoryAutoSync({ domain: '', matched_category: '' })
        setRemarks('')
        setRemarksDraft('')
        setIsRemarksEditing(true)
        setActivityRefreshKey(0)
    }, [leadOnly])

    const applyMemberProfileFallback = useCallback(() => {
        const draft = profileDraftFromMember(member)
        setCustomerData({
            name: draft.name,
            business_name: draft.business_name,
            business_category: draft.business_category,
            matched_category: draft.matched_category,
            domain: draft.domain,
            preferred_language: draft.preferred_language,
            rating: draft.rating,
            starred: draft.star,
            primary_address: {
                locality: draft.address,
                city: draft.city,
                pin_code: draft.pincode,
            },
            _fromMemberFallback: true,
        })
        setProfileDraft(draft)
        setRemarks('')
        setRemarksDraft('')
        setError('Full customer record could not be loaded — showing batch row data. Save remarks after profile syncs.')
    }, [member])

    const loadCustomerData = useCallback(async (customerId, { phoneHint, retried = false, authHdrs, silent = false } = {}) => {
        if (silent) setRefreshing(true)
        else setLoading(true)
        setError(null)
        const headers = authHdrs || authHeaders
        try {
            const res = await fetch(`${customerApiBase}/customers/${customerId}/details`, { headers })
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                if (res.status === 404 && !retried) {
                    const phone = phoneHint || member?.phone_e164 || member?.phone
                    const resolvedId = phone
                        ? await resolveCustomerIdByPhone(customerApiBase, phone, headers)
                        : null
                    if (resolvedId && resolvedId !== Number(customerId)) {
                        setActiveCustomerId(resolvedId)
                        await loadCustomerData(resolvedId, { phoneHint: phone, retried: true, authHdrs: headers })
                        return
                    }
                    applyMemberProfileFallback()
                    return
                }
                throw new Error(body.detail || `Failed to load customer (${res.status})`)
            }
            const data = await res.json()
            const cust = data.customer || {}
            const addr = data.primary_address || {}

            setCustomerData({ ...cust, primary_address: addr })
            setProfileDraft({
                name: cust.name || '',
                phone: cust.phone || '',
                non_wa_phone: cust.non_whatsapp_phone || member?.non_wa_phone || member?.dialed_phone || '',
                business_name: cust.business_name || '',
                business_category: cust.business_category || '',
                matched_category: cust.matched_category || '',
                domain: cust.domain || '',
                address: addr.locality || '',
                city: addr.city || '',
                pincode: addr.pin_code || '',
                preferred_language: cust.preferred_language || 'kannada',
                star: cust.starred || cust.star || false,
                rating: cust.rating ?? 5,
            })
            setProfileCategoryAutoSync({ domain: '', matched_category: '' })
            const text = cust.remarks || ''
            setRemarks(text)
            setRemarksDraft(text)
            setIsRemarksEditing(true)
        } catch (err) {
            setError(err.message || 'Failed to load customer')
        } finally {
            if (silent) setRefreshing(false)
            else setLoading(false)
        }
    }, [customerApiBase, member, authHeaders, applyMemberProfileFallback])

    useEffect(() => {
        if (!isOpen || !memberRef.current) return
        let cancelled = false
        resetState()

        const openProfile = async () => {
            const m = memberRef.current
            const phone = m.phone_e164 || m.phone
            const custFromRow = m?.customer_id ? Number(m.customer_id) : null
            const initCid = leadOnly ? null : (m?.customer_id ? Number(m.customer_id) : null)

            if (leadId && leadOnly) {
                setLoading(false)
                return
            }
            if (custFromRow) {
                setActiveCustomerId(custFromRow)
                await loadCustomerData(custFromRow, { phoneHint: phone })
                return
            }
            if (initCid) {
                await loadCustomerData(initCid, { phoneHint: phone })
                return
            }
            const resolvedId = phone
                ? await resolveCustomerIdByPhone(customerApiBase, phone, authHeaders)
                : null
            if (cancelled) return
            if (resolvedId) {
                setActiveCustomerId(resolvedId)
                await loadCustomerData(resolvedId, { phoneHint: phone })
                return
            }
            applyMemberProfileFallback()
            setLoading(false)
        }
        openProfile()
        return () => { cancelled = true }
    // Only re-init when panel opens or a different row is selected — not on parent re-renders / convert patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetState uses memberRef; avoid wiping draft on poll/tick
    }, [isOpen, memberKey])

    useEffect(() => {
        if (!isOpen || !showConvert) return undefined
        return runAutoMatcher(
            leadDraft.business_category,
            leadDraft.business_name,
            leadDraft.name,
            setLeadCategoryAutoSync,
        )
    }, [isOpen, showConvert, memberKey, leadDraft.business_category, leadDraft.business_name, leadDraft.name, runAutoMatcher])

    useEffect(() => {
        if (!isOpen || !effectiveCustomerId || showConvert || !isEditing) return undefined
        return runAutoMatcher(
            profileDraft.business_category,
            profileDraft.business_name,
            profileDraft.name,
            setProfileCategoryAutoSync,
        )
    }, [isOpen, effectiveCustomerId, showConvert, isEditing, memberKey, profileDraft.business_category, profileDraft.business_name, profileDraft.name, runAutoMatcher])

    const handleSaveRemarks = async () => {
        if (!effectiveCustomerId) return
        setRemarksSaving(true)
        setError(null)
        try {
            const res = await fetch(`${customerApiBase}/customers/${effectiveCustomerId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({ remarks: remarksDraft }),
            })
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body.detail || `Save failed (${res.status})`)
            }
            setRemarks(remarksDraft)
            setIsRemarksEditing(false)
            onRemarksSaved?.({ customer_id: effectiveCustomerId })
            showToast?.('Remarks saved', 'success')
        } catch (err) {
            setError(err.message || 'Failed to save remarks')
        } finally {
            setRemarksSaving(false)
        }
    }

    const handleSaveProfile = async () => {
        if (!effectiveCustomerId) return
        setSaving(true)
        setError(null)
        try {
            const payload = {
                name: profileDraft.name,
                business_name: profileDraft.business_name,
                business_category: profileDraft.business_category,
                domain: profileDraft.domain?.trim() || profileCategoryAutoSync.domain?.trim() || undefined,
                matched_category: profileDraft.matched_category?.trim() || profileCategoryAutoSync.matched_category?.trim() || undefined,
                preferred_language: profileDraft.preferred_language,
                rating: profileDraft.rating,
                star: profileDraft.star,
            }
            const res = await fetch(`${customerApiBase}/customers/${effectiveCustomerId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload),
            })
            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body.detail || `Save failed (${res.status})`)
            }
            setCustomerData((prev) => ({ ...prev, ...profileDraft }))
            setIsEditing(false)
            showToast?.('Profile updated', 'success')
            onRemarksSaved?.({ customer_id: effectiveCustomerId })
        } catch (err) {
            setError(err.message || 'Failed to save profile')
        } finally {
            setSaving(false)
        }
    }

    const handleCreateCustomer = async () => {
        if (isNwMember) {
            if (!resolveNonWhatsappLeadId(member, batchForNw)) {
                setError('Cannot convert — missing non-WhatsApp lead id.')
                return
            }
            if (!normalizeCapturedWhatsapp(leadDraft.captured_whatsapp_number)) {
                setError('Enter captured WhatsApp number (10 digits).')
                return
            }
        } else if (!leadId && !memberPhone) {
            setError('Cannot convert — no lead_id or phone on this member.')
            return
        }
        setConvertBusy(true)
        setError(null)
        setConvertMsg(null)
        try {
            const memberForConvert = {
                ...member,
                ...leadDraft,
                non_whatsapp_lead_id: resolveNonWhatsappLeadId(member, batchForNw),
            }
            let data
            if (isNwMember) {
                data = await convertNonWhatsappMemberFromLead(memberForConvert, leadDraft, {
                    customerApiBase,
                    authToken,
                    batchId: batchId || member?.batch_id,
                    marketingInitiated,
                    postAction: false,
                })
            } else {
                const body = {
                    lead_id: leadId || undefined,
                    phone: memberPhone || undefined,
                    name: leadDraft.name?.trim() || undefined,
                    business_name: leadDraft.business_name?.trim() || undefined,
                    business_category: leadDraft.business_category?.trim() || undefined,
                    domain: leadDraft.domain?.trim() || leadCategoryAutoSync.domain?.trim() || undefined,
                    matched_category: leadDraft.matched_category?.trim() || leadCategoryAutoSync.matched_category?.trim() || undefined,
                    address: leadDraft.address?.trim() || undefined,
                    city: leadDraft.city?.trim() || undefined,
                    pincode: leadDraft.pincode?.trim() || undefined,
                    preferred_language: leadDraft.preferred_language || undefined,
                    rating: leadDraft.rating ?? 5,
                    star: leadDraft.star ?? false,
                }
                const res = await fetch(`${customerApiBase}/customers/convert-from-lead`, {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify(body),
                })
                if (!res.ok) {
                    const payload = await res.json().catch(() => ({}))
                    throw new Error(payload.detail || `Convert failed (${res.status})`)
                }
                data = await res.json()
            }
            const customerId = Number(data.customer_id)
            if (!customerId) throw new Error('Convert succeeded but no customer_id returned')
            const waE164 = data.phone_e164 || formatCapturedWhatsappE164(leadDraft.captured_whatsapp_number)
            const nwLeadId = data.non_whatsapp_lead_id ?? resolveNonWhatsappLeadId(member, batchForNw)

            setActiveCustomerId(customerId)
            if (data.lead_updated === false) {
                setConvertMsg(`Customer already linked: #${customerId}`)
            } else if (data.customer_created === false) {
                setConvertMsg(`Linked to existing customer: #${customerId}`)
            } else {
                setConvertMsg(`Customer created: #${customerId}`)
            }

            const effectiveBatchId = batchId || member?.batch_id
            const effectiveMemberId = member?.id || member?.member_id
            if (effectiveBatchId && effectiveMemberId) {
                try {
                    const actionResult = await postMemberAction(effectiveBatchId, effectiveMemberId, {
                        action: 'create_customer',
                        marketing_initiated: marketingInitiated,
                    })
                    const waPatch = memberPatchFromAutoWhatsapp(actionResult) || {}
                    onActionRecorded?.({
                        customer_id: customerId,
                        lead_id: data.lead_id ? Number(data.lead_id) : undefined,
                        phone_e164: waE164,
                        phone: waE164,
                        dialed_phone: data.non_whatsapp_phone || dialedNonWaPhone,
                        non_wa_phone: data.non_whatsapp_phone || dialedNonWaPhone,
                        non_whatsapp_lead_id: nwLeadId ? Number(nwLeadId) : null,
                        captured_whatsapp_number: normalizeCapturedWhatsapp(leadDraft.captured_whatsapp_number),
                        outcome: 'customer_created',
                        outcome_label: 'Customer created',
                        outcome_at: new Date().toISOString(),
                        deferActioned: true,
                        ...waPatch,
                    })
                    setActivityRefreshKey((k) => k + 1)
                } catch {
                    /* audit-log best-effort */
                }
            }
            onConverted?.({
                customer_id: customerId,
                user_id: data.user_id,
                lead_id: data.lead_id,
                phone_e164: waE164,
                dialed_phone: data.non_whatsapp_phone || dialedNonWaPhone,
                non_whatsapp_lead_id: nwLeadId,
                ...data,
            })
            setIsEditing(false)
            await loadCustomerData(customerId, { phoneHint: waE164 || memberPhone, retried: true, silent: true })
        } catch (err) {
            setError(err.message || 'Failed to create customer')
        } finally {
            setConvertBusy(false)
        }
    }

    if (!isOpen || !member) return null

    const memberBatchId = batchId || member?.batch_id
    const memberRowId = member?.id || member?.member_id

    return (
        <div className="mcp-overlay" onClick={onClose}>
            <div className="mcp-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="mcp-header">
                    <div className="mcp-header-info">
                        <h2 className="mcp-title">{memberDisplayName(member)}</h2>
                        <p className="mcp-subtitle">
                            {member.phone || member.phone_e164 || '—'}
                            {memberEntitySubtitleExtra(
                                { ...member, customer_id: effectiveCustomerId || member?.customer_id },
                                batchForNw,
                            )}
                        </p>
                        {convertMsg ? (
                            <div className="mcp-convert-banner" role="status">{convertMsg}</div>
                        ) : null}
                    </div>
                    <button type="button" className="mcp-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className={`mcp-body-wrap${refreshing ? ' mcp-body-wrap--refreshing' : ''}`}>
                    {loading ? (
                        <div className="mcp-loading-overlay" aria-hidden={!loading}>
                            <Loader2 size={20} className="spin" />
                        </div>
                    ) : null}
                    <div className="mcp-body">
                        {showConvert ? (
                            <div className="mcp-section">
                                <div className="mcp-section-head mcp-section-head--spaced">
                                    <span className="mcp-section-title">Create customer</span>
                                    {isNwMember ? (
                                        <span className="mcp-lead-badge">Non-WA #{member?.non_whatsapp_lead_id || leadId}</span>
                                    ) : leadId ? (
                                        <span className="mcp-lead-badge">Lead #{leadId}</span>
                                    ) : null}
                                </div>
                                {isNwMember ? (
                                    <>
                                        <div className="mcp-field">
                                            <label htmlFor="lead-dialed-phone">Dialed (non-WA)</label>
                                            <input
                                                id="lead-dialed-phone"
                                                value={dialedNonWaPhone}
                                                readOnly
                                                className="mcp-field-readonly"
                                            />
                                        </div>
                                        <div className="mcp-field">
                                            <label htmlFor="lead-captured-wa">Captured WhatsApp number *</label>
                                            <input
                                                id="lead-captured-wa"
                                                value={leadDraft.captured_whatsapp_number || ''}
                                                placeholder="10-digit mobile"
                                                onChange={(e) => setLeadDraft((p) => ({
                                                    ...p,
                                                    captured_whatsapp_number: e.target.value,
                                                }))}
                                            />
                                        </div>
                                    </>
                                ) : null}
                                <ProfileFormFields
                                    draft={leadDraft}
                                    setDraft={setLeadDraft}
                                    categoryAutoSync={leadCategoryAutoSync}
                                    taxonomy={taxonomy}
                                    phoneReadonly
                                    hidePhone={isNwMember}
                                    idPrefix="lead"
                                />
                                <div className="mcp-form-actions">
                                    <button
                                        type="button"
                                        className="mcp-create-btn"
                                        onClick={handleCreateCustomer}
                                        disabled={
                                            convertBusy
                                            || (isNwMember
                                                ? !(member?.non_whatsapp_lead_id || leadId)
                                                : (!leadId && !memberPhone))
                                        }
                                    >
                                        {convertBusy ? <Loader2 size={16} className="spin" /> : <UserPlus size={16} />}
                                        {convertBusy ? 'Creating…' : 'Create Customer'}
                                    </button>
                                </div>
                                <div className="mcp-locked-hint">
                                    <strong>Remarks unlock after conversion.</strong>
                                    Create the customer above to add and save remarks.
                                </div>
                                {error ? <div className="mcp-banner-error">{error}</div> : null}
                            </div>
                        ) : null}

                        {!showConvert && (effectiveCustomerId || customerData) ? (
                            <div className="mcp-section">
                                <div className="mcp-section-head">
                                    <span className="mcp-section-title">Customer profile</span>
                                    {!isEditing ? (
                                        <button type="button" className="mcp-edit-btn" onClick={() => setIsEditing(true)}>
                                            <Pencil size={14} /> Edit
                                        </button>
                                    ) : null}
                                </div>
                                {isEditing ? (
                                    <>
                                        <ProfileFormFields
                                            draft={profileDraft}
                                            setDraft={setProfileDraft}
                                            categoryAutoSync={profileCategoryAutoSync}
                                            taxonomy={taxonomy}
                                            phoneReadonly
                                            idPrefix="cust"
                                        />
                                        <div className="mcp-form-actions">
                                            <button type="button" className="mcp-save" onClick={handleSaveProfile} disabled={saving || !effectiveCustomerId}>
                                                {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                                                {saving ? 'Saving…' : 'Save'}
                                            </button>
                                            <button
                                                type="button"
                                                className="mcp-cancel"
                                                onClick={() => {
                                                    setProfileDraft({
                                                        name: customerData?.name || profileDraft.name || '',
                                                        phone: customerData?.phone || profileDraft.phone || '',
                                                        business_name: customerData?.business_name || profileDraft.business_name || '',
                                                        business_category: customerData?.business_category || profileDraft.business_category || '',
                                                        matched_category: customerData?.matched_category || profileDraft.matched_category || '',
                                                        domain: customerData?.domain || profileDraft.domain || '',
                                                        address: customerData?.primary_address?.locality || profileDraft.address || '',
                                                        city: customerData?.primary_address?.city || profileDraft.city || '',
                                                        pincode: customerData?.primary_address?.pin_code || profileDraft.pincode || '',
                                                        preferred_language: customerData?.preferred_language || profileDraft.preferred_language || 'kannada',
                                                        star: customerData?.starred || customerData?.star || profileDraft.star || false,
                                                        rating: customerData?.rating ?? profileDraft.rating ?? 5,
                                                    })
                                                    setProfileCategoryAutoSync({ domain: '', matched_category: '' })
                                                    setIsEditing(false)
                                                }}
                                                disabled={saving}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <ProfileDisplayGrid
                                        draft={profileDraft}
                                        nonWaPhone={profileDraft.non_wa_phone || customerData?.non_whatsapp_phone || dialedNonWaPhone}
                                    />
                                )}
                                {error && !showConvert ? <div className="mcp-banner-error">{error}</div> : null}
                            </div>
                        ) : null}

                        {!showConvert && effectiveCustomerId ? (
                            <div className="mcp-section">
                                <span className="mcp-section-title">Remarks</span>
                                {customerData?._fromMemberFallback ? (
                                    <p className="mcp-locked-hint">Profile loaded from batch row — remarks still save to customer #{effectiveCustomerId}.</p>
                                ) : null}
                                <div className="mcp-remarks-section">
                                    <textarea
                                        className="mcp-textarea"
                                        value={remarksDraft}
                                        onChange={(e) => setRemarksDraft(e.target.value)}
                                        placeholder="Add remarks after the call"
                                    />
                                    <div className="mcp-form-actions">
                                        <button type="button" className="mcp-save" onClick={handleSaveRemarks} disabled={remarksSaving}>
                                            {remarksSaving ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                                            {remarksSaving ? 'Saving…' : 'Save remarks'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {!effectiveCustomerId && !showConvert && !customerData && !leadId && !memberPhone ? (
                            <div className="mcp-locked-hint">
                                <strong>No lead or customer linked.</strong>
                                Link a lead or customer to edit profile and remarks.
                            </div>
                        ) : null}
                    </div>

                    {memberBatchId && memberRowId ? (
                        <div className="mcp-action-footer">
                            <div className="mcp-action-footer-log">
                                <MemberActivityLog
                                    phone={member?.phone_e164 || member?.phone}
                                    batchId={memberBatchId}
                                    refreshKey={activityRefreshKey}
                                    scrollable
                                />
                            </div>
                            <div className="mcp-action-footer-actions">
                                <MemberRowActions
                                    batchId={memberBatchId}
                                    batch={batchForNw}
                                    member={{ ...member, customer_id: effectiveCustomerId || null }}
                                    variant="sheet"
                                    showToast={showToast}
                                    marketingInitiated={marketingInitiated}
                                    showCall={showCall}
                                    showCreateCustomer={false}
                                    onRefresh={() => setActivityRefreshKey((k) => k + 1)}
                                    onOutcomeChange={(patch) => {
                                        setActivityRefreshKey((k) => k + 1)
                                        onActionRecorded?.(patch)
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    )
}
